// Intelligent Prefetching Service - Netflix-style preloading
// Prefetches next likely actions before user clicks

import { logger } from './logger';
import { prefetchRoute, preloadRoute } from './smartPrefetch';
import { aiCoach } from '../services/aiCoach';
import { spacedRepetitionService } from '../services/spacedRepetitionService';
import { questionLoader } from '../services/questionLoader';
import { serviceWorkerManager } from '../services/serviceWorkerManager';

/**
 * NETFLIX-STYLE: Prefetch next 3-5 tests when user starts current test
 * This makes navigation feel instant - like Netflix preloading next episodes
 * Preloads multiple tests ahead, not just the next one
 */
export function prefetchNextTestOnStart(currentTestId: string, language: string = 'en'): void {
  if (typeof window === 'undefined') return;

  // Use requestIdleCallback to avoid blocking main thread
  if ('requestIdleCallback' in window) {
    requestIdleCallback(() => {
      // NETFLIX PATTERN: Get next 3-5 recommendations (not just 1)
      const recommendations = getNextTests(currentTestId, 5); // Preload next 5 tests
      
      recommendations.forEach((rec, index) => {
        if (rec?.testId) {
          const nextTestUrl = `/practice/${rec.testId}`;
          
          // Preload route (higher priority than prefetch)
          // First test gets immediate preload, others get prefetch
          if (index === 0) {
            preloadRoute(nextTestUrl);
          } else {
            prefetchRoute(nextTestUrl);
          }
          
          // Prefetch questions for each test (staggered to avoid blocking)
          setTimeout(() => {
            prefetchTestQuestions(rec.testId, language);
          }, index * 200); // Stagger by 200ms each
        }
      });
      
      logger.debug(`✅ NETFLIX-STYLE: Prefetched ${recommendations.length} tests ahead`);
    }, { timeout: 2000 });
  } else {
    // Fallback
    setTimeout(() => {
      const recommendations = getNextTests(currentTestId, 5);
      recommendations.forEach((rec, index) => {
        if (rec?.testId) {
          const nextTestUrl = `/practice/${rec.testId}`;
          if (index === 0) {
            preloadRoute(nextTestUrl);
          } else {
            prefetchRoute(nextTestUrl);
          }
          setTimeout(() => {
            prefetchTestQuestions(rec.testId, language);
          }, index * 200);
        }
      });
    }, 1000);
  }
}

/**
 * Get next N tests (Netflix-style: multiple tests ahead)
 * Returns array of test recommendations, avoiding duplicates and already-taken tests
 */
function getNextTests(currentTestId: string, count: number = 5): Array<{ testId: string; name: string }> {
  const testHistory = aiCoach.getTestHistory();
  const takenTestIds = new Set(testHistory.map(result => result.testId));
  takenTestIds.add(currentTestId); // Don't include current test
  
  const recommendations: Array<{ testId: string; name: string }> = [];
  const seenTestIds = new Set<string>();
  
  // 1. Get primary recommendation (AI Coach top recommendation)
  const topRec = aiCoach.getTopRecommendation();
  if (topRec?.testId && !takenTestIds.has(topRec.testId) && !seenTestIds.has(topRec.testId)) {
    recommendations.push({ testId: topRec.testId, name: topRec.testName });
    seenTestIds.add(topRec.testId);
  }
  
  // 2. Get Netflix-style recommendations (weakest topics)
  if (recommendations.length < count) {
    const netflixRecs = getNetflixRecommendations(currentTestId, count - recommendations.length, takenTestIds, seenTestIds);
    netflixRecs.forEach(rec => {
      if (rec?.testId && !takenTestIds.has(rec.testId) && !seenTestIds.has(rec.testId)) {
        recommendations.push(rec);
        seenTestIds.add(rec.testId);
      }
    });
  }
  
  // 3. Fill remaining slots with available tests (prioritize untaken tests)
  if (recommendations.length < count) {
    const allTestIds = [
      'traffic-lights-signals', 'priority-rules', 'hazard-perception', 'speed-safety',
      'road-signs', 'mandatory-signs', 'prohibitory-signs', 'warning-signs',
      'bicycle-interactions', 'roundabout-rules', 'tram-interactions', 'pedestrian-crossings',
      'construction-zones', 'weather-conditions', 'vehicle-knowledge', 'parking-rules',
      'environmental', 'technology-safety', 'alcohol-drugs', 'fatigue-rest',
      'emergency-procedures'
    ];
    
    allTestIds.forEach(testId => {
      if (recommendations.length >= count) return;
      if (!takenTestIds.has(testId) && !seenTestIds.has(testId) && testId !== currentTestId) {
        recommendations.push({ testId, name: testId });
        seenTestIds.add(testId);
      }
    });
  }
  
  return recommendations.slice(0, count);
}

/**
 * Get Netflix-style recommendations (tests focused on weakest topics)
 */
function getNetflixRecommendations(
  currentTestId: string, 
  count: number,
  takenTestIds: Set<string>,
  seenTestIds: Set<string>
): Array<{ testId: string; name: string }> {
  try {
    // Import netflixStyleRecommendation dynamically to avoid circular dependencies
    const { netflixStyleRecommendation } = require('../services/netflixStyleRecommendation');
    const recommendations: Array<{ testId: string; name: string }> = [];
    
    // Get multiple recommendations by calling getNextTest multiple times
    // Each call should return a different test based on weakest topics
    // We'll temporarily add seen tests to avoid duplicates
    const tempSeen = new Set(seenTestIds);
    
    for (let i = 0; i < count * 3 && recommendations.length < count; i++) {
      const rec = netflixStyleRecommendation.getNextTest(currentTestId);
      if (rec?.testId && !takenTestIds.has(rec.testId) && !tempSeen.has(rec.testId)) {
        recommendations.push(rec);
        tempSeen.add(rec.testId);
      }
    }
    
    return recommendations.slice(0, count);
  } catch (error) {
    logger.warn('Error getting Netflix recommendations:', error);
    return [];
  }
}

/**
 * Prefetch test questions in background
 */
async function prefetchTestQuestions(testId: string, language: string): Promise<void> {
  try {
    // Load questions (will be cached)
    const questions = await questionLoader.loadQuestions(testId, language as any, {
      useCache: true,
      saveToCache: true
    });
    
    // Cache in Service Worker for offline use
    if (questions.length > 0 && serviceWorkerManager.isActive()) {
      serviceWorkerManager.cacheQuestions(testId, language, questions);
    }
    
    logger.debug('Prefetched questions for test:', testId);
  } catch (error) {
    logger.error('Error prefetching test questions:', error);
  }
}

/**
 * Prefetch recommendation test when user views dashboard
 */
export function prefetchRecommendationOnDashboard(): void {
  if (typeof window === 'undefined') return;

  if ('requestIdleCallback' in window) {
    requestIdleCallback(() => {
      const recommendation = aiCoach.getTopRecommendation();
      if (recommendation?.testId) {
        const testUrl = `/practice/${recommendation.testId}`;
        prefetchRoute(testUrl);
        logger.debug('Prefetched recommendation from dashboard:', recommendation.testId);
      }
    }, { timeout: 2000 });
  } else {
    setTimeout(() => {
      const recommendation = aiCoach.getTopRecommendation();
      if (recommendation?.testId) {
        prefetchRoute(`/practice/${recommendation.testId}`);
      }
    }, 1000);
  }
}

/**
 * Prefetch spaced repetition reviews when user views dashboard
 */
export async function prefetchDueReviews(language: string = 'en'): Promise<void> {
  if (typeof window === 'undefined') return;

  try {
    const dueReviews = await spacedRepetitionService.getDueReviews();
    
    // Prefetch top 3 due reviews
    const top3 = dueReviews.slice(0, 3);
    
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => {
        top3.forEach(review => {
          const testUrl = `/practice/${review.testId}`;
          prefetchRoute(testUrl);
          prefetchTestQuestions(review.testId, language);
        });
        logger.debug('Prefetched due reviews:', top3.length);
      }, { timeout: 3000 });
    } else {
      setTimeout(() => {
        top3.forEach(review => {
          prefetchRoute(`/practice/${review.testId}`);
          prefetchTestQuestions(review.testId, language);
        });
      }, 1500);
    }
  } catch (error) {
    logger.error('Error prefetching due reviews:', error);
  }
}

/**
 * Prefetch translations for likely next language
 */
export function prefetchTranslations(currentLanguage: string): void {
  if (typeof window === 'undefined') return;

  const languages = ['en', 'nl', 'ar'];
  const otherLanguages = languages.filter(lang => lang !== currentLanguage);
  
  // Prefetch top 2 other languages
  if ('requestIdleCallback' in window) {
    requestIdleCallback(() => {
      otherLanguages.slice(0, 2).forEach(lang => {
        // Prefetch translation files
        const translationUrl = `/i18n/locales/${lang}.json`;
        prefetchRoute(translationUrl);
      });
      logger.debug('Prefetched translations for:', otherLanguages.slice(0, 2));
    }, { timeout: 2000 });
  }
}









